import React, { useMemo } from 'react'
import { Card, Table, Button, Badge, Select, message, Popconfirm } from 'antd'
import { PageHeader, CommonFilters, CommonSelectorProps, CommonSelector } from '@components'
import { ColumnProps, TableProps } from 'antd/lib/table'
import { formatTime } from '@library'
import { FieldItem } from 'src/components/CommonFilters/configs'
import { useTableService } from '@hooks/useTableService'
import { Link } from 'react-router-dom'
import { getSortOrder } from '@configs/table'
import { AIQualityCheckTaskListItem, AIQualityCheckTaskListFilter } from 'src/types/ai-quality-check'
import { antdSortMap } from '@configs/map'
import { getQualityCheckTaskList, updateQualityCheckTaskStatus, deleteQualityCheckTask } from '@services/ai-quality-check'
import { TaskStatus, ActionMode } from '../quality-check-scheme-detail/constants'
import { sortTypeEnum } from '@types'

const qcTaskName: CommonSelectorProps<AIQualityCheckTaskListFilter, AIQualityCheckTaskListItem> = {
  id: 'qcTaskName',
  name: '任务名称',
  service: getQualityCheckTaskList,
  valueKey: 'taskName',
  labelKey: 'taskName',
  showSearch: true,
  searchKey: 'taskName',
  placeholderOnSearching: '按任务名称模糊搜索...',
}

const taskFilterItems: FieldItem[] = [
  {
    fieldName: 'taskName',
    label: '任务名称',
    render: () => <CommonSelector {...qcTaskName} />,
  },
  {
    fieldName: 'status',
    label: '任务状态',
    render: () => {
      const options = [
        { label: '启用中', value: TaskStatus.RUNNING },
        { label: '未启用', value: TaskStatus.STOP },
      ]
      return (<Select allowClear options={options} />)
    },
    handleValue: val => {
      if (!val && val !== TaskStatus.STOP) return { status: undefined }
      return {
        status: val === TaskStatus.RUNNING ? [TaskStatus.RUNNING] : [TaskStatus.STOP, TaskStatus.STEP_ONE, TaskStatus.STEP_TWO],
      }
    },
  },
  {
    fieldName: 'timeRange',
    label: '更新时间',
    type: 'rangePicker',
    handleValue: val => ({
      updateTimeFrom: formatTime(val?.[0], 'start'),
      updateTimeTo: formatTime(val?.[1], 'end'),
    }),
  },
]

const initFilter: AIQualityCheckTaskListFilter = {
  page: 1,
  pageSize: 10,
  sort: 'updateTime',
  sortType: sortTypeEnum.DESC,
}
const QualityCheckManagementPage = () => {
  const {
    loading,
    list: taskList,
    filter,
    pagination,
    rowKey,
    setFilter,
    resetFilter,
  } = useTableService(getQualityCheckTaskList, initFilter, '获取质检任务列表失败')

  const updateTask = async (taskId: number, status: number) => {
    if (status === TaskStatus.STEP_ONE || status === TaskStatus.STEP_TWO) {
      message.warning('请先完善质检任务配置再启用')
      return
    }
    const [res, err] = await updateQualityCheckTaskStatus({
      taskId,
      status: status === TaskStatus.RUNNING ? TaskStatus.STOP : TaskStatus.RUNNING,
    })
    if (err) {
      message.error(`更新任务失败: ${err.message}`)
      return
    }
    setFilter({ ...filter, page: 1 })
  }

  const deleteTask = async (taskId: number) => {
    const [res, err] = await deleteQualityCheckTask({ taskId })
    if (err) {
      message.error(`删除任务失败: ${err.message}`)
      return
    }
    message.success('删除任务成功')
    setFilter({ ...filter, page: 1 })
  }

  const columns: ColumnProps<AIQualityCheckTaskListItem>[] = useMemo(() => [
    {
      title: 'ID',
      dataIndex: 'taskId',
      width: 40,
    },
    {
      title: '任务名称',
      dataIndex: 'taskName',
      width: 150,
    },
    {
      title: '质检方案',
      dataIndex: 'planNames',
      width: 120,
      render: (planNames: string[], record) => {
        return planNames?.join(',')
      },
    },
    {
      title: '运行模式',
      dataIndex: 'taskType',
      width: 100,
      render: (taskType: number, record) => {
        return taskType === ActionMode.offline ? '存量数据-异步任务' : (record?.runType === 0 ? '增量数据-实时数据' : '增量数据-异步任务')
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 150,
      sorter: true,
      sortOrder: filter.sort === 'createTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      width: 150,
      sorter: true,
      sortOrder: filter.sort === 'updateTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '执行情况',
      dataIndex: 'completionRate',
      width: 100,
      render: (completionRate: number, record) => {
        return `${completionRate}%(共${record.total})`
      },
    },
    {
      title: '任务状态',
      dataIndex: 'status',
      width: 80,
      render: (status: number, record) => {
        const text = status === TaskStatus.RUNNING ? '启用中' : '未启用'
        const color = status === TaskStatus.RUNNING ? '#52C41A' : '#D9D9D9'
        return <Badge key={record.taskId} color={color} text={text} />
      },
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 200,
      render: (action, record) => (
        <div style={{ display: 'flex' }}>
          <Link className="mr10" target="_blank" to={`/config/quality-check-task-detail/${record.taskId}?taskName=${record.taskName}&updateTime=${record.updateTime}`}>
            任务详情
          </Link>
          <div
            className="mr10"
            style={{ color: record.status === TaskStatus.RUNNING ? '#E54545' : '#29CC85', cursor: 'pointer' }}
            onClick={() => {
              updateTask(record.taskId, record.status)
            }}
          >
            {record.status === TaskStatus.RUNNING ? '停用' : '启用'}
          </div>
          {record.status === TaskStatus.RUNNING ? (
            <Link className="mr10" target="_blank" to={`/config/quality-check-create/${record.taskId}?status=${TaskStatus.RUNNING}`}>
              查看
            </Link>
          ) : (
            <Link className="mr10" target="_blank" to={`/config/quality-check-create/${record.taskId}?status=${record.status}`}>
              编辑
            </Link>
          )}
          <Popconfirm
            title={`确定要删除任务"${record.taskName}"吗？删除后不可恢复。`}
            onConfirm={() => deleteTask(record.taskId)}
            okText="确认删除"
            cancelText="取消"
          >
            <div
              style={{
                color: '#E54545',
                cursor: 'pointer',
              }}
            >
              删除
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ], [filter])

  const onTableChange: TableProps<AIQualityCheckTaskListItem>['onChange'] = (pagination, filters, sorter) => {
    const sort = getSortOrder<AIQualityCheckTaskListFilter>(sorter)
    setFilter(sort)
  }

  return (
    <>
      <PageHeader title="质检任务管理" />
      <Card className="mt20 mb20">
        <CommonFilters
          className="custom-Label"
          itemList={taskFilterItems}
          formLayout={{
            labelCol: { span: 8 },
            wrapperCol: { span: 16 },
          }}
          onSubmit={filter => setFilter({ ...filter, page: 1 })}
          onReset={() => resetFilter(initFilter)}
        />
      </Card>
      <Card className="mt20">
        <Button>
          <Link target="_blank" to="/config/quality-check-create">
            创建任务
          </Link>
        </Button>
        <Table
          className="mt20"
          loading={loading}
          columns={columns}
          dataSource={taskList}
          pagination={pagination}
          rowKey={rowKey}
          onChange={onTableChange}
        />
      </Card>
    </>
  )
}

export default QualityCheckManagementPage
